/*
Used to improve the login for users
*/

/*import React, { createContext, useContext, useState, useEffect } from "react";
import { fetchCurrentUser } from "./authApi";
import keycloak from "./keycloak";

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    try {
      const user = await fetchCurrentUser();
      setCurrentUser(user);
      return user;
    } catch (err) {
      console.error("Failed to fetch user:", err);
      setCurrentUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUser();

    // Listen for auth changes
    if (keycloak) {
      keycloak.onAuthSuccess = refreshUser;
      keycloak.onAuthRefreshSuccess = refreshUser;
    }
  }, []);

  return (
    <UserContext.Provider value={{ currentUser, loading, refreshUser }}>
      {children}
    </UserContext.Provider>
  );
}

export function useCurrentUser() {
  return useContext(UserContext);
}*/

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { fetchCurrentUser } from "./authApi";
import keycloak from "./keycloak";
import { useBackendReady } from "./BackendReadyContext";
import { showToast } from "./utils/toastManager";
import useInactivityLogout from "./useInactivityLogout";

const UserContext = createContext(null);

function forceLogout(message) {
  showToast(message, "warning");

  // Clear keycloak callback state before logout
  Object.keys(localStorage)
    .filter((k) => k.startsWith("kc-callback-"))
    .forEach((k) => localStorage.removeItem(k));

  try {
    keycloak.logout({
      redirectUri:
        (process.env.REACT_APP_SITE_URL || window.location.origin) +
        "/ProviderSearchPage",
    });
  } catch {
    window.location.href = "/ProviderSearchPage";
  }
}

export function UserProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const backendReady = useBackendReady();

  const refreshUser = useCallback(async () => {
    if (!keycloak || !keycloak.authenticated) {
      setCurrentUser(null);
      setLoading(false);
      return null;
    }

    try {
      const user = await fetchCurrentUser();
      setCurrentUser(user);
      return user;
    } catch (err) {
      if (err.sessionExpired) {
        forceLogout("Session expired. Please log in again.");
        return null;
      }
      console.error("UserContext: Failed to fetch user:", err);
      setCurrentUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch user once both backend is ready AND keycloak is authenticated
  useEffect(() => {
    if (!backendReady) return;

    if (keycloak?.authenticated) {
      refreshUser();
    } else {
      setLoading(false);
    }
  }, [backendReady, refreshUser]);

  // Listen for auth events (login, token refresh, logout)
  useEffect(() => {
    if (!keycloak) return;

    const onAuthSuccess = () => {
      if (backendReady) {
        refreshUser();
      }
    };

    const onAuthRefreshSuccess = () => {
      if (backendReady) {
        refreshUser();
      }
    };

    const onAuthLogout = () => {
      setCurrentUser(null);
    };

    keycloak.onAuthSuccess = onAuthSuccess;
    keycloak.onAuthRefreshSuccess = onAuthRefreshSuccess;
    keycloak.onAuthLogout = onAuthLogout;

    return () => {
      keycloak.onAuthSuccess = null;
      keycloak.onAuthRefreshSuccess = null;
      keycloak.onAuthLogout = null;
    };
  }, [backendReady, refreshUser]);

  // Log out after 10 minutes of inactivity (only when authenticated)
  useInactivityLogout(!!currentUser);

  return (
    <UserContext.Provider value={{ currentUser, loading, refreshUser }}>
      {children}
    </UserContext.Provider>
  );
}

export function useCurrentUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useCurrentUser must be used within a UserProvider");
  }
  return context;
}
